<template>
  <div>
    <div class="align-items-center mb-1 d-flex flex-row justify-content-between">
      <div
        id="summary-title-order-amount-payfast"
        data-testid="summary-title-order-amount-payfast"
        class="font-14 text-placeholder"
      >
        {{ $t('payment.airWallex.labelOrderAmount') }}:
      </div>
      <strong
        id="summary-payfast-order-amount"
        data-testid="summary-payfast-order-amount"
      >
        {{ currencyFormat(amount) }} USD
      </strong>
    </div>

    <div class="align-items-center mb-1 d-flex flex-row justify-content-between">
      <div
        id="summany-payfast-title-payment-method"
        data-testid="summany-payfast-title-payment-method"
        class="font-14 text-placeholder"
      >
        {{ $t('addFundPage.creditCardPayment.textPaymentMethod') }}:
      </div>

      <strong
        id="summary-payfast-method"
        data-testid="summary-payfast-method"
        class="font-14 text-primary"
      >
        <img
          :src="payfastLogo"
          height="16px"
          alt="PayFast"
          style="margin-right: 5px"
        >
        PayFast
      </strong>
    </div>

    <div
      v-if="percentFee"
      class="d-flex align-items-center flex-row justify-content-between mb-1"
    >
      <div
        id="summary-airwallex-title-service-fee"
        data-testid="summary-airwallex-title-service-fee"
        class="font-14 text-placeholder"
      >
        {{ $t('payment.balanceTransactionsTable.textServiceCharge') }}
        <small
          class="font-small-1"
          style="margin-right: 6px"
        >
          {{ `(${percentFeeTxt})` }}:
        </small>
      </div>
      <strong
        id="summary-payfast-current-service-charge"
        data-testid="summary-payfast-current-service-charge"
        class="text-primary"
      >{{ currencyFormat(serviceCharge) }} USD</strong>
    </div>

    <hr class="hr-custom">

    <div class="d-flex align-items-center justify-content-between mb-1">
      <div
        id="title-total-amount-payfast"
        data-testid="title-total-amount-payfast"
        class="font-16 font-medium"
      >
        Subtotal
      </div>
      <strong
        v-if="amountValidate"
        id="current-total-amount-payfast"
        data-testid="current-total-amount-payfast"
      >
        {{ subTotal.toLocaleString('en-US') }} USD
      </strong>
      <strong v-else>
        --
      </strong>
    </div>

    <div class="d-flex align-items-center justify-content-between mb-1">
      <div
        id="title-total-amount-payfast"
        data-testid="title-total-amount-payfast"
        class="font-14 text-placeholder"
      >
        Converted subtotal
      </div>
      <strong
        v-if="amountValidate"
        id="current-total-amount-payfast"
        data-testid="current-total-amount-payfast"
      >
        {{ subTotalTxt }} PKR
      </strong>
      <strong v-else>
        --
      </strong>
    </div>

    <div
      v-if="methodFee"
      class="d-flex align-items-center justify-content-between mb-1"
    >
      <div
        id="title-payfast-fee"
        data-testid="title-payfast-fee"
        class="font-14 text-placeholder"
      >
        {{ $t('payment.paypal.textPaymentFee', {payment: "PayFast"}) }}
        <small
          id="percent-payfast-fee"
          data-testid="percent-payfast-fee"
          class="font-small-1"
          style="margin-right: 6px"
        >
          {{ `(${methodFeeTxt})` }}
        </small>
      </div>
      <div>
        <div
          v-if="amountValidate"
          class="d-flex align-items-center"
        >
          <strong
            id="current-payfast-fee"
            data-testid="current-payfast-fee"
            class="text-primary"
          >{{ payfastFeeTxt }} PKR</strong>
        </div>
        <strong v-else>
          --
        </strong>
      </div>
    </div>

    <div
      v-if="taxRate"
      class="d-flex align-items-center justify-content-between mb-1"
    >
      <div
        id="title-tax-fee-payfast"
        data-testid="title-tax-fee-payfast"
        class="font-14 text-placeholder"
      >
        {{ $t('payment.balanceTransactionsTable.textTax') }}
        <small
          id="percent-tax-payfast-fee"
          data-testid="percent-tax-payfast-fee"
          class="font-small-1"
          style="margin-right: 6px"
        >
          {{ `(${percentTaxTxt})` }}
        </small>
      </div>
      <div>
        <div
          v-if="amountValidate"
          class="d-flex align-items-center"
        >
          <strong
            id="current-tax-fee-payfast"
            data-testid="current-tax-fee-payfast"
            class="text-primary"
          >{{ convertUSDtoPKR(tax).toLocaleString('en-US') }} PKR</strong>
        </div>
        <strong v-else>
          --
        </strong>
      </div>
    </div>

    <hr class="hr-custom">

    <div class="d-flex align-items-center justify-content-between mb-3">
      <div class="font-24 text-placeholder">
        {{ $t('addFundPage.creditCardPayment.textTotalAmount') }}
      </div>
      <strong
        v-if="amountValidate"
        id="amount"
        class="font-24 total-amount"
      >
        {{ totalAmount.toLocaleString('en-US') }} PKR
      </strong>
      <strong v-else>
        --
      </strong>
    </div>
  </div>
</template>

<script>
import paymentMixin from '@/mixins/paymentMixin'
import generalConfigsMixin from '@/mixins/generalConfigsMixin'
import numberFormatMixin from '@/mixins/numberFormatMixin'

// plugins
import { createNamespacedHelpers } from 'vuex'
import NP from 'number-precision'

const { mapGetters: mapGettersAuth } = createNamespacedHelpers('auth')

export default {

  mixins: [paymentMixin, generalConfigsMixin, numberFormatMixin],

  data() {
    return {
      amount: Number(this.$route.query.amount) || '',
      bankTransferAmount: this.$route.query.bankTransferAmount || '',
      paymentMethod: this.$route.query.paymentMethod || '',
    }
  },

  computed: {
    ...mapGettersAuth(['user', 'generalConfigs']),

    isInTrial() {
      return this.user?.isInTrial
    },

    percentFee() {
      return this.user?.data?.plans?.transferFee?.payfast || 0
    },

    percentFeeTxt() {
      return `${(this.percentFee * 100).toFixed(2)}%`
    },

    methodFee() {
      return this.generalConfigs?.paymentGateFee?.payfastGate || 0
    },

    methodFeeTxt() {
      return `${(this.methodFee * 100).toFixed(2)}%`
    },

    percentTaxTxt() {
      return `${(this.taxRate * 100).toFixed(2)}%`
    },

    tax() {
      return NP.times(this.taxRate, this.amount)
    },

    subTotal() {
      return this.amount + this.serviceCharge + this.tax
    },

    subTotalTxt() {
      return this.convertUSDtoPKR(this.subTotal - this.tax).toLocaleString('en-US')
    },

    totalAmount() {
      return NP.divide(Math.floor(NP.times(NP.plus(this.convertUSDtoPKR(this.subTotal), this.payfastFee), 100)), 100)
    },

    serviceCharge() {
      return this.percentFee * this.amount
    },

    payfastFee() {
      return this.methodFee * this.convertUSDtoPKR(this.subTotal)
    },

    payfastFeeTxt() {
      return (this.totalAmount - this.convertUSDtoPKR(this.subTotal)).toLocaleString('en-US')
    },

    amountValidate() {
      return !!(this.amount >= this.minTopUp && this.amount <= this.maxAmount)
    },

    maxAmount() {
      return 100000
    },

    payfastLogo() {
      // eslint-disable-next-line global-require
      return require('@/assets/images/pages/payment/ic-payfast.jpg')
    },
  },
}
</script>

<style lang="scss" scoped>
.summary-container {
  padding: 20px;
  background-color: #F5F5F7;
  // border: 1px solid rgba(22, 33, 62, 0.1);
  border-radius: 16px;

  .title-summary{
    margin-bottom: 21px;
  }
}
.transfer {
  max-width: 125px;
}

.total-amount{
  color: #00A9DE;
}
</style>
